import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase';
import SwapResume from '../components/faction/SwapResume';
import FactionShareButton from '../components/faction/FactionShareButton';
import WrestlerGrid from '../components/wrestler/WrestlerGrid';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import CreditIcon from '../components/CreditIcon';
import { mergeFactionWrestlersWithDetails } from '../utils/factionWrestlers';
import {
  calculateFactionTotalScore,
  hasFactionScoringStarted
} from '../utils/factionScoring';
import { calculateGmMatchPointsByFaction, normalizeGmMatch } from '../utils/gmMatches';
import { autoCloseExpiredGmMatches } from '../utils/gmMatchSync';

function FactionDetails() {
  const { id: factionId } = useParams();
  const [faction, setFaction] = useState(null);
  const [members, setMembers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [wrestlerMap, setWrestlerMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [ownerProfile, setOwnerProfile] = useState({
    profileImageUrl: null
  });
  const [rosterId, setRosterId] = useState(null);
  const [rosterName, setRosterName] = useState('');
  const [rosterGameType, setRosterGameType] = useState('');
  const [rosterData, setRosterData] = useState(null);
  const scoringStarted = hasFactionScoringStarted(faction);
  const factionRosterId = rosterId || faction?.rosterId || null;
  const factionRosterName = rosterName || faction?.roster || 'Non assegnato';
  const factionGameType = rosterGameType || faction?.gameType || '';

  const baseBadgeStyle = {
    fontSize: '0.85rem',
    backdropFilter: 'blur(10px)'
  };

  const rosterBadgeStyle = {
    ...baseBadgeStyle,
    backgroundColor: 'rgba(255, 255, 255, 0.68)',
    color: '#111',
    border: '1px solid var(--brand-primary)'
  };

  const getGameTypeBadgeStyle = gameType => ({
    ...baseBadgeStyle,
    backgroundColor:
      gameType === 'Road To'
        ? 'rgba(220, 53, 69, 0.18)'
        : gameType === 'Indipendente'
        ? 'rgba(255,255,255,0.14)'
        : gameType === 'Competitivo'
        ? 'rgba(255, 193, 7, 0.16)'
        : 'rgba(255,255,255,0.14)',
    color:
      gameType === 'Road To'
        ? '#dc3545'
        : gameType === 'Indipendente'
        ? '#fff'
        : gameType === 'Competitivo'
        ? 'var(--brand-secondary)'
        : '#fff',
    border:
      gameType === 'Road To'
        ? '1px solid #dc3545'
        : gameType === 'Indipendente'
        ? '1px solid rgba(255,255,255,0.24)'
        : gameType === 'Competitivo'
        ? '1px solid var(--brand-secondary)'
        : '1px solid rgba(255,255,255,0.24)'
  });

  const creditsBadgeStyle = {
    ...baseBadgeStyle,
    backgroundColor: 'rgba(0, 128, 17, 0.25)',
    color: 'var(--money)',
    border: '1px solid var(--money)'
  };

  const neutralBadgeStyle = {
    ...baseBadgeStyle,
    backgroundColor: 'rgba(255,255,255,0.12)',
    border: '1px solid rgba(255,255,255,0.24)',
    color: '#fff'
  };

  const formatBadgeDate = value => {
    let dateObj = null;

    if (typeof value?.toDate === 'function') {
      dateObj = value.toDate();
    } else if (value instanceof Date) {
      dateObj = value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      dateObj = new Date(value);
    } else if (value?.seconds) {
      dateObj = new Date(value.seconds * 1000);
    }

    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
      return 'Data non valida';
    }

    return dateObj.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Timeout per caricamenti troppo lunghi
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        setError('Il caricamento sta impiegando piu tempo del previsto');
        setLoading(false);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load faction document
        const fRef = doc(db, 'factions', factionId);
        const fSnap = await getDoc(fRef);

        if (!fSnap.exists()) {
          setError('Fazione non trovata');
          setLoading(false);
          return;
        }

        const fData = fSnap.data();
        const factionRosterName = fData.roster || '';
        const factionRosterId = fData.rosterId || null;
        let loadedRosterIsGM = false;
        let loadedRosterData = null;

        if (factionRosterId) {
          try {
            const rosterSnap = await getDoc(doc(db, 'rosters', factionRosterId));
            if (rosterSnap.exists()) {
              const rosterData = rosterSnap.data();
              loadedRosterData = { id: rosterSnap.id, ...rosterData };
              loadedRosterIsGM = Boolean(rosterData.isGM);
              setRosterData(loadedRosterData);
              setRosterId(rosterSnap.id);
              setRosterName(rosterData.name || factionRosterName || '');
              setRosterGameType(rosterData.gameType || '');
            } else {
              setRosterData(null);
              setRosterId(factionRosterId);
              setRosterName(factionRosterName || '');
              setRosterGameType(fData.gameType || '');
            }
          } catch (err) {
            console.error('Errore nel caricamento roster:', err);
            setRosterData(null);
            setRosterId(factionRosterId);
            setRosterName(factionRosterName || '');
            setRosterGameType(fData.gameType || '');
          }
        } else {
          setRosterData(null);
          setRosterId(null);
          setRosterName(factionRosterName || '');
          setRosterGameType(fData.gameType || '');
        }

        // Fetch owner profile
        if (fData.ownerUid) {
          try {
            const oSnap = await getDoc(doc(db, 'users', fData.ownerUid));
            if (oSnap.exists()) {
              const userData = oSnap.data();
              setOwnerProfile({
                username: userData.username || '—',
                profileImageUrl: userData.profileImageUrl || null,
                email: userData.email
              });
            }
          } catch (err) {
            console.error('Errore nel caricamento owner:', err);
          }
        }

        // Build wrestler map
        let map = {};
        try {
          const wSnap = await getDocs(collection(db, 'wrestlers'));
          map = {};
          wSnap.docs.forEach(wDoc => {
            const w = wDoc.data();
            const championImages = Array.isArray(w.championImages) ? w.championImages : [];
            const championTypes = Array.isArray(w.championTypes) ? w.championTypes : [];

            map[wDoc.id] = {
              name: w.name || w.nome || 'Sconosciuto',
              score: w.score || 0,
              imageUrl: w.imageUrl || '',
              brand: w.brand || '',
              championImages: championImages,
              championImage: championImages.length > 0 ? championImages[0] : null,
              championTypes: championTypes,
              championType: championTypes.length > 0 ? championTypes[0] : null,
              gender: w.gender || null,
              updatedAt: w.updatedAt || null
            };
          });
          setWrestlerMap(map);
        } catch (err) {
          console.error('Errore nel caricamento wrestler:', err);
          throw new Error('Errore nel caricamento dei wrestler');
        }

        // Prepare members list
        const membersList = mergeFactionWrestlersWithDetails(fData, map).map(member => ({
          ...member,
          championImage: member.championImages?.length > 0 ? member.championImages[0] : null,
          championType: member.championTypes?.length > 0 ? member.championTypes[0] : null,
          updatedAt: map[member.id]?.updatedAt || null
        }));
        const effectiveFactionData = {
          ...fData,
          dataInizioPunteggio:
            fData.dataInizioPunteggio || loadedRosterData?.dataInizioPunteggio || null
        };

        // Compute totalScore
        let totalScore = calculateFactionTotalScore({
          faction: effectiveFactionData,
          roster: loadedRosterData,
          members: membersList
        });
        if (loadedRosterIsGM && factionRosterId) {
          const matchesSnap = await getDocs(
            collection(db, 'rosters', factionRosterId, 'matches')
          );
          const normalizedMatches = matchesSnap.docs.map(matchDoc =>
            normalizeGmMatch(matchDoc)
          );
          const { matches } = await autoCloseExpiredGmMatches({
            db,
            rosterId: factionRosterId,
            matches: normalizedMatches,
            roster: loadedRosterData,
            persist: true
          });
          totalScore = calculateGmMatchPointsByFaction(matches)[fSnap.id] || 0;
        }
        const creditsLeft = fData.creditsLeft ?? 0;

        // createdAt conversion
        let createdAt = null;
        if (fData.createdAt) {
          if (typeof fData.createdAt.toDate === 'function') createdAt = fData.createdAt.toDate();
          else if (fData.createdAt instanceof Date) createdAt = fData.createdAt;
          else if (fData.createdAt.seconds) createdAt = new Date(fData.createdAt.seconds * 1000);
        }

        setFaction({
          ...effectiveFactionData,
          id: fSnap.id,
          name: effectiveFactionData.name,
          imageUrl: effectiveFactionData.imageUrl || '',
          ownerUid: effectiveFactionData.ownerUid || null,
          rosterId: factionRosterId,
          roster: loadedRosterData?.name || factionRosterName,
          gameType: loadedRosterData?.gameType || effectiveFactionData.gameType || '',
          hasBackstage: loadedRosterData?.hasBackstage ?? effectiveFactionData.hasBackstage,
          backstageCount:
            loadedRosterData?.backstageCount ?? effectiveFactionData.backstageCount,
          hasMainEventer:
            loadedRosterData?.hasMainEventer ?? effectiveFactionData.hasMainEventer,
          isGM: loadedRosterIsGM,
          dataInizioPunteggio: effectiveFactionData.dataInizioPunteggio,
          totalScore,
          creditsLeft,
          createdAt,
          maxWrestlers: effectiveFactionData.maxWrestlers || 0,
          usedCredits: (effectiveFactionData.initialCredits || 0) - creditsLeft
        });
        setMembers(membersList);

        // Load assignments
        try {
          const aSnap = await getDocs(collection(db, 'factions', factionId, 'assignments'));
          const asg = aSnap.docs.map(doc => {
            const d = doc.data();
            const name = map[d.wrestlerId]?.name || 'Sconosciuto';
            const points = d.active
              ? (map[d.wrestlerId]?.score || 0) - (d.startScore || 0)
              : (d.endScore || 0) - (d.startScore || 0);
            return {
              id: doc.id,
              name,
              points,
              active: d.active,
              date: (d.active ? d.acquiredAt : d.releasedAt)?.toDate()
            };
          });
          setAssignments(asg);
        } catch (err) {
          console.error('Errore nel caricamento assignments:', err);
          // Continuiamo anche senza assignments
        }

        setLoading(false);
      } catch (err) {
        console.error('Errore nel caricamento fazione:', err);
        setError(err.message || 'Errore nel caricamento della fazione');
        setLoading(false);
      }
    };

    fetchData();
  }, [factionId]);

  if (loading) {
    return <LoadingSpinner fullPage={true} />;
  }

  if (error) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-danger mx-auto" style={{ maxWidth: '500px' }}>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
        <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
          <button
            className="btn btn-dark d-flex align-items-center"
            onClick={() => window.location.reload()}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Ricarica
          </button>
          <Link to="/factions" className="btn btn-outline-dark d-flex align-items-center">
            <i className="bi bi-arrow-left me-2"></i>
            Torna alle fazioni
          </Link>
        </div>
      </div>
    );
  }

  if (!faction) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-warning mx-auto" style={{ maxWidth: '500px' }}>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          Fazione non trovata
        </div>
        <Link
          to="/factions"
          className="btn btn-outline-dark mt-3 d-flex align-items-center mx-auto"
          style={{ width: 'fit-content' }}
        >
          <i className="bi bi-arrow-left me-2"></i>
          Torna alle fazioni
        </Link>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      {/* Header Hero - Versione PC migliorata */}
      <div
        className="faction-hero position-relative overflow-hidden py-5 py-lg-6 px-3 px-md-4 px-lg-5"
        style={{
          minHeight: '380px',
          borderBottom: '4px solid var(--brand-secondary)'
        }}
      >
        <div
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{
            background:
              'linear-gradient(45deg, var(--brand-primary) 55%, var(--brand-primary-light) 100%)',
            zIndex: 0
          }}
        />

        {faction.imageUrl && (
          <>
            <div
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{
                zIndex: 0
              }}
            />
            <img
              src={faction.imageUrl}
              alt=""
              aria-hidden="true"
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{
                objectFit: 'cover',
                objectPosition: 'center',
                filter: 'blur(1px) brightness(1)',
                opacity: 0.4,
                transform: 'scale(1.22)',
                zIndex: 0
              }}
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{
                background: 'linear-gradient(45deg, rgba(38, 56, 72, 0.5',
                zIndex: 1
              }}
            />
          </>
        )}

        <div className="container position-relative h-100" style={{ zIndex: 2 }}>
          <div className="row align-items-center h-100 position-relative" style={{ zIndex: 1 }}>
            {/* Logo Fazione - Sinistra (PC: piu grande e visibile) */}
            {/* Wrapper responsivo centrato */}
            <div className="d-flex justify-content-center">
              <div
                className="faction-logo-wrapper rounded-4 d-flex align-items-center justify-content-center"
                style={{
                  width: '100%',
                  maxWidth: '200px',
                  aspectRatio: '1 / 1',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  overflow: 'hidden'
                }}
              >
                {faction.imageUrl ? (
                  <img
                    src={faction.imageUrl}
                    alt={`Logo ${faction.name}`}
                    className="img-fluid rounded-4 shadow-lg"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
                      transition: 'transform 0.3s ease'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  />
                ) : (
                  <div
                    className="faction-logo-placeholder d-flex align-items-center justify-content-center w-100 h-100"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: '3px dashed rgba(255,255,255,0.3)',
                      borderRadius: '1rem'
                    }}
                  >
                    <i
                      className="bi bi-people-fill text-white opacity-50"
                      style={{ fontSize: 'clamp(2rem, 8vw, 3rem)' }}
                    ></i>
                  </div>
                )}
              </div>
            </div>

            {/* Titolo sopra per PC, si sposta a destra */}
            <div className="col-12 d-md-none mb-4 text-center">
              <h1 className="faction-name display-5 fw-bold" style={{ color: 'white' }}>
                {faction.name}
              </h1>
            </div>

            {/* Info Centro - Espansa per PC */}
            <div className="col-12 col-md-6 col-lg-7 mb-4 mb-md-0 ps-lg-5">
              <div className="faction-info">
                {/* Titolo nascosto su mobile, visibile su PC accanto ai badge */}
                <div className="d-none d-md-block mb-4">
                  <h1 className="faction-name display-4 fw-bold mb-3" style={{ color: 'white' }}>
                    {faction.name}
                  </h1>
                </div>

                <div className="faction-badges d-flex flex-wrap gap-3 mb-4 justify-content-center justify-content-md-start">
                  {faction?.rosterId ? (
                    <Link
                      to={`/rosters/${factionRosterId}`}
                      className="badge rounded-pill px-3 py-1 d-inline-flex align-items-center text-decoration-none"
                      style={{
                        ...rosterBadgeStyle,
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = rosterBadgeStyle.backgroundColor;
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <i className="bi bi-diagram-2-fill me-1"></i>
                      {factionRosterName}
                    </Link>
                  ) : (
                    <span
                      className="badge rounded-pill px-3 py-1 d-inline-flex align-items-center"
                      style={rosterBadgeStyle}
                    >
                      <i className="bi bi-diagram-2-fill me-1"></i>
                      {factionRosterName}
                    </span>
                  )}

                  <span
                    className="badge rounded-pill px-3 py-1 d-inline-flex align-items-center"
                    style={getGameTypeBadgeStyle(factionGameType)}
                  >
                    <i className="bi bi-joystick me-1"></i>
                    {factionGameType || 'N/A'}
                  </span>

                  {faction.dataInizioPunteggio && (
                    <span
                      className="badge rounded-pill px-3 py-1 d-inline-flex align-items-center"
                      style={neutralBadgeStyle}
                    >
                      <i className="bi bi-calendar-event me-1"></i>
                      Inizio: {formatBadgeDate(faction.dataInizioPunteggio)}
                    </span>
                  )}
                </div>

                <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center justify-content-md-start">
                  <FactionShareButton
                    faction={faction}
                    members={members}
                    roster={rosterData}
                    buttonClassName="btn btn-light"
                    buttonLabel="Condividi"
                    title="Condividi questa fazione"
                  />
                </div>

                {/* Info Proprietario - Migliorata per PC */}
                <div
                  className="owner-info d-flex align-items-center p-2 rounded-4"
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(2px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    cursor: faction?.ownerUid ? 'pointer' : 'default'
                  }}
                  onClick={() => faction?.ownerUid && navigate(`/users/${faction.ownerUid}`)}
                  role={faction?.ownerUid ? 'button' : undefined}
                  tabIndex={faction?.ownerUid ? 0 : undefined}
                  onKeyDown={event => {
                    if (faction?.ownerUid && (event.key === 'Enter' || event.key === ' ')) {
                      event.preventDefault();
                      navigate(`/users/${faction.ownerUid}`);
                    }
                  }}
                >
                  {ownerProfile.profileImageUrl ? (
                    <img
                      src={ownerProfile.profileImageUrl}
                      alt={`Profilo ${ownerProfile.username}`}
                      className="rounded-circle me-3 shadow-lg"
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        border: '3px solid white'
                      }}
                    />
                  ) : (
                    <div
                      className="rounded-circle me-3 d-flex align-items-center justify-content-center shadow-lg"
                      style={{
                        width: '60px',
                        height: '60px',
                        backgroundColor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        border: '3px solid white'
                      }}
                    >
                      <i className="bi bi-person-fill" style={{ fontSize: '1.8rem' }}></i>
                    </div>
                  )}
                  <div className="flex-grow-1">
                    <div className="small text-white opacity-75 mb-1">Proprietario</div>
                    <div className="text-white fw-bold fs-5 mb-1">{ownerProfile.username}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Punteggio - Destra (PC: piu prominente) */}
            <div className="col-12 col-md-3 col-lg-3">
              <div
                className="total-score-card text-center p-4 p-lg-5 rounded-5 shadow-lg"
                style={{
                  border: '5px solid white',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(2px)',
                  minHeight: '220px'
                }}
              >
                {/* Effetto glow */}
                <div
                  className="position-absolute top-0 start-0 w-100 h-100"
                  style={{
                    background:
                      'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 60%)',
                    animation: 'pulse 4s ease-in-out infinite alternate'
                  }}
                ></div>

                <div className="position-relative">
                  <div
                    className="score-value display-1 fw-bold lh-1 mb-2 text-brand-secondary"
                    style={{
                      textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                      fontSize: 'clamp(3rem, 5vw, 4.5rem)'
                    }}
                  >
                    {faction.totalScore}
                  </div>
                  <div
                    className="score-unit small mb-4"
                    style={{
                      opacity: 0.8,
                      fontSize: '1.1rem'
                    }}
                  >
                    Punti
                  </div>

                  <div className="mt-3 pt-3 border-top border-white border-opacity-25">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="text-start ">
                        <div className="small opacity-75 mb-1">Wrestler</div>
                        <div className="fw-bold fs-5">{members.length}</div>
                      </div>
                      <div className="text-end">
                        <div className="small opacity-75 mb-1">Crediti</div>
                        <div className="fw-bold fs-5">{faction.creditsLeft}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorazioni per PC */}
        <div
          className="position-absolute bottom-0 start-0 w-100 h-25 d-none d-lg-block"
          style={{
            background: 'linear-gradient(0deg, rgba(0,0,0,0.2) 0%, transparent 100%)',
            pointerEvents: 'none',
            zIndex: 1
          }}
        ></div>
      </div>

      {/* Contenuto Principale - Layout a griglia per PC */}
      <div className="container py-2 py-lg-12">
        <div className="row">
          {/* Sezione Membri (PC: 2/3 larghezza) */}
          <div className="col-12 col-lg-12 mb-5 mb-lg-0">
            <div className="pe-lg-4">
              <WrestlerGrid
                members={members}
                faction={faction}
                roster={rosterData}
                factionName={faction.name}
                scoringStarted={scoringStarted}
              />
            </div>
          </div>

          {/* Sidebar per PC (1/3 larghezza) */}
          <div className="col-12">
            {/* Storia degli scambi */}
            <div className="mb-3 mt-2">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-0">
                  <SwapResume factionId={factionId} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FactionDetails;
